import { useEffect, useRef } from 'react';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Stack,
  Typography,
} from '@mui/material';

import { useExternalKeysStore } from '../../stores/externalKeysStore';

type ExternalKeysDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const ExternalKeysDialog = ({ open, onClose }: ExternalKeysDialogProps) => {
  const keys = useExternalKeysStore((state) => state.keys);
  const isLoading = useExternalKeysStore((state) => state.isLoading);
  const deletingKeyId = useExternalKeysStore((state) => state.deletingKeyId);
  const error = useExternalKeysStore((state) => state.error);
  const loadKeys = useExternalKeysStore((state) => state.loadKeys);
  const deleteKey = useExternalKeysStore((state) => state.deleteKey);
  const resetTransientState = useExternalKeysStore((state) => state.resetTransientState);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      void loadKeys();
    }

    if (!open && wasOpenRef.current) {
      resetTransientState();
    }

    wasOpenRef.current = open;
  }, [open, loadKeys, resetTransientState]);

  const handleClose = () => {
    if (isLoading || deletingKeyId) {
      return;
    }

    resetTransientState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>My External Keys</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {isLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : keys.length > 0 ? (
            <Stack spacing={1.5}>
              {keys.map((key) => {
                const isDeleting = deletingKeyId === key.id;

                return (
                  <Box
                    key={key.id}
                    sx={{
                      border: '2px solid #e7e7e7',
                      borderRadius: '5px',
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>{key.tokenType}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Authenticated as {key.externalUsername}
                      </Typography>
                    </Box>
                    <IconButton
                      color="error"
                      onClick={() => void deleteKey(key.id)}
                      disabled={Boolean(deletingKeyId)}
                      aria-label={`Delete ${key.tokenType} key`}
                    >
                      {isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteOutlineOutlinedIcon />}
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography color="text.secondary">
              dotstorming is not currently connected to any external applications
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading || Boolean(deletingKeyId)} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
